import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonDivider,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';

const DashboardSkeletonShell = styled(SkeletonPageShell)`
    max-width: 1280px;
    margin: 0 auto;
`;

const SolutionsCarouselTrack = styled.div`
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) 36px;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    min-width: 0;
`;

const SolutionsCardsTrack = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    height: 130px;
    overflow: hidden;
    min-width: 0;
`;

const SolutionTrackCard = styled(SkeletonSurface)<{ $isActive?: boolean }>`
    display: flex;
    align-items: center;
    gap: 14px;
    width: 240px;
    flex: 0 0 240px;
    padding: 20px 24px;
    border-radius: 12px;
    min-height: 80px;
    opacity: ${(props) => (props.$isActive ? 1 : 0.72)};

    @media (max-width: 760px) {
        &:not(:first-child) {
            display: none;
        }
    }
`;

const CarouselDotsSkeleton = styled(SkeletonRow)`
    justify-content: center;
    margin-bottom: 24px;
`;

const CarouselDetailSkeleton = styled(SkeletonSurface)`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: center;
    gap: 40px;
    min-height: 380px;
    padding: 40px;
    border-radius: 16px;

    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const HighlightedToolsSkeletonTrack = styled.div`
    display: flex;
    gap: 16px;
    overflow: hidden;
    padding: 4px 0;
`;

const HighlightedToolSkeletonCard = styled(SkeletonSurface)`
    flex: 0 0 240px;
    padding: 20px;
    border-radius: 12px;
`;

const SectionSkeleton = styled.section`
    margin-top: 24px;
    padding-top: 8px;
`;

const ResourceSkeletonCard = styled(SkeletonSurface)`
    padding: 28px 20px;
    border-radius: 12px;
    min-height: 160px;
`;

export const DashboardPageSkeleton: FC = () => (
    <DashboardSkeletonShell aria-hidden='true'>
        <SkeletonPageHeader $margin='20px 0 48px'>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(320px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='14px' />
            </SkeletonStack>
        </SkeletonPageHeader>

        <SolutionsCarouselTrack>
            <SkeletonBlock $width='36px' $height='36px' $round />
            <SolutionsCardsTrack>
                {Array.from({ length: 5 }).map((_, index) => (
                    <SolutionTrackCard key={index} $isActive={index === 2}>
                        <SkeletonBlock $width='32px' $height='32px' $radius='8px' />
                        <SkeletonBlock $width={index === 1 ? '150px' : '120px'} $height='16px' />
                    </SolutionTrackCard>
                ))}
            </SolutionsCardsTrack>
            <SkeletonBlock $width='36px' $height='36px' $round />
        </SolutionsCarouselTrack>

        <CarouselDotsSkeleton $gap='8px'>
            {Array.from({ length: 5 }).map((_, index) => (
                <SkeletonBlock key={index} $width={index === 2 ? '10px' : '8px'} $height={index === 2 ? '10px' : '8px'} $round />
            ))}
        </CarouselDotsSkeleton>

        <CarouselDetailSkeleton>
            <SkeletonBlock $height='300px' $radius='12px' />
            <SkeletonStack $gap='18px'>
                <SkeletonBlock $width='min(300px, 100%)' $height='24px' />
                <SkeletonStack $gap='10px'>
                    <SkeletonBlock $width='100%' $height='13px' />
                    <SkeletonBlock $width='94%' $height='13px' />
                    <SkeletonBlock $width='88%' $height='13px' />
                    <SkeletonBlock $width='72%' $height='13px' />
                </SkeletonStack>
                <SkeletonRow $gap='16px' $margin='6px 0 0'>
                    <SkeletonBlock $width='160px' $height='38px' $radius='19px' />
                    <SkeletonBlock $width='86px' $height='14px' />
                </SkeletonRow>
            </SkeletonStack>
        </CarouselDetailSkeleton>

        <SkeletonDivider $margin='48px 0 0' />
        <SectionSkeleton>
            <SkeletonBlock $width='190px' $height='22px' $margin='0 0 16px' />
            <HighlightedToolsSkeletonTrack>
                {Array.from({ length: 6 }).map((_, index) => (
                    <HighlightedToolSkeletonCard key={index}>
                        <SkeletonStack $gap='12px'>
                            <SkeletonBlock $width='40px' $height='40px' $radius='10px' />
                            <SkeletonBlock $width={index % 2 === 0 ? '170px' : '130px'} $height='16px' />
                            <SkeletonBlock $width='100%' $height='12px' />
                            <SkeletonBlock $width='76%' $height='12px' />
                        </SkeletonStack>
                    </HighlightedToolSkeletonCard>
                ))}
            </HighlightedToolsSkeletonTrack>
        </SectionSkeleton>

        <SkeletonDivider $margin='48px 0 0' />
        <SectionSkeleton>
            <SkeletonBlock $width='100px' $height='22px' $margin='0 0 16px' />
            <SkeletonResponsiveGrid $columns='repeat(4, minmax(0, 1fr))' $gap='16px' $tabletColumns='repeat(2, minmax(0, 1fr))'>
                {Array.from({ length: 4 }).map((_, index) => (
                    <ResourceSkeletonCard key={index}>
                        <SkeletonStack $gap='12px' $align='center'>
                            <SkeletonBlock $width='44px' $height='44px' $radius='12px' />
                            <SkeletonBlock $width={index === 1 ? '180px' : '120px'} $height='16px' />
                            <SkeletonBlock $width='85%' $height='12px' />
                            <SkeletonBlock $width='70%' $height='12px' />
                        </SkeletonStack>
                    </ResourceSkeletonCard>
                ))}
            </SkeletonResponsiveGrid>
        </SectionSkeleton>
    </DashboardSkeletonShell>
);
